<template>
  <section class="equipment-lab">
    <header class="equipment-lab__hero">
      <div>
        <span class="eyebrow">{{ copy('Equipment package matrix', 'Matrice des packages d’équipement') }}</span>
        <h2>{{ copy('One Item. Two Add-ons. One explicit job.', 'Un item. Deux add-ons. Un rôle explicite.') }}</h2>
        <p>{{ copy(
          'Every active non-event combination is compiled once, scored inside each useful scenario, then matched against the complete four-perk build.',
          'Chaque combinaison active hors événement est compilée une fois, notée dans chaque scénario utile, puis confrontée au build complet de quatre perks.'
        ) }}</p>
      </div>
      <div class="equipment-lab__stats">
        <article><strong>{{ metrics.packages }}</strong><span>{{ copy('analysed packages', 'packages analysés') }}</span></article>
        <article><strong>{{ metrics.scenarios }}</strong><span>{{ copy('gameplay scenarios', 'scénarios de gameplay') }}</span></article>
        <article><strong>{{ metrics.rouletteReady }}</strong><span>{{ copy('roulette-ready', 'prêts pour la roulette') }}</span></article>
      </div>
    </header>

    <div class="equipment-lab__controls">
      <label class="equipment-search">
        <span>{{ copy('Search a package', 'Chercher un package') }}</span>
        <input v-model.trim="search" type="search" :placeholder="copy('Item, Add-on, scenario…', 'Item, add-on, scénario…')">
      </label>
      <label>
        <span>{{ copy('Scenario', 'Scénario') }}</span>
        <select v-model="scenarioFilter">
          <option value="all">{{ copy('Best scenario for each package', 'Meilleur scénario de chaque package') }}</option>
          <option v-for="scenario in availableScenarios" :key="scenario.id" :value="scenario.id">{{ scenarioName(scenario) }}</option>
        </select>
      </label>
    </div>

    <div class="category-tabs" role="group" :aria-label="copy('Item category', 'Catégorie d’item')">
      <button type="button" :class="{ 'is-active': categoryFilter === 'all' }" @click="setCategory('all')">{{ copy('All', 'Tout') }}</button>
      <button
        v-for="category in categories"
        :key="category"
        type="button"
        :class="{ 'is-active': categoryFilter === String(category) }"
        @click="setCategory(String(category))"
      >
        {{ categoryName(category) }}
      </button>
    </div>

    <div class="package-ledger">
      <article v-for="entry in displayedPackages" :key="entry.package.id" class="package-card">
        <header class="package-card__header">
          <span class="package-card__icon">
            <img v-if="itemIcon(entry.package.item)" :src="itemIcon(entry.package.item)" alt="" decoding="async">
            <b v-else>{{ initials(entry.package.item.name) }}</b>
          </span>
          <div>
            <small>{{ categoryName(entry.package.category) }}</small>
            <h3>{{ entryName(entry.package.item, 'item') }}</h3>
            <span :class="['scenario-tier', `is-${entry.result.tier}`]">{{ tierLabel(entry.result.tier) }}</span>
          </div>
          <div class="strength-dial">
            <small>{{ copy('Potential', 'Potentiel') }}</small>
            <strong>{{ formatScore(entry.result.score) }}</strong>
            <span>/100</span>
          </div>
        </header>

        <div class="package-card__scenario">
          <small>{{ copy('Intended scenario', 'Scénario visé') }}</small>
          <strong>{{ scenarioName(entry.result.scenario) }}</strong>
          <p>{{ scenarioDescription(entry.result.scenario) }}</p>
        </div>

        <div class="addon-pair">
          <div v-for="addon in entry.package.addons" :key="addon.id">
            <span>
              <img v-if="addonIcon(addon)" :src="addonIcon(addon)" alt="" decoding="async">
              <b v-else>{{ initials(addon.name) }}</b>
            </span>
            <strong>{{ entryName(addon, 'addon') }}</strong>
          </div>
        </div>

        <div class="package-card__metrics">
          <span><small>{{ copy('Consistency', 'Régularité') }}</small><strong>{{ entry.package.consistency }}</strong></span>
          <span><small>{{ copy('Versatility', 'Polyvalence') }}</small><strong>{{ entry.package.versatility }}</strong></span>
          <span><small>{{ copy('Evidence', 'Confiance') }}</small><strong>{{ entry.package.confidence }}</strong></span>
        </div>

        <div v-if="entry.result.metrics.length" class="potential-metrics">
          <span v-for="metric in entry.result.metrics.slice(0, 4)" :key="metric.id">
            <small>{{ metricLabel(metric.id) }}</small>
            <strong>{{ metric.value }}{{ metric.suffix || metricSuffix(metric.id) }}</strong>
          </span>
        </div>

        <details class="package-card__details">
          <summary>{{ copy('Use conditions and limits', 'Conditions d’usage et limites') }}</summary>
          <p>{{ scenarioCondition(entry.result.scenario) }}</p>
          <div v-if="entry.package.risks.length" class="risk-list">
            <span v-for="risk in entry.package.risks" :key="risk">{{ riskLabel(risk) }}</span>
          </div>
          <small>{{ entry.result.curated ? copy('Curated pair evidence', 'Paire documentée manuellement') : copy('Mechanics-derived evaluation', 'Évaluation dérivée des mécaniques') }}</small>
          <a :href="sourceUrl(entry.package.category)" target="_blank" rel="noopener noreferrer">{{ copy('Official mechanics', 'Mécaniques officielles') }}</a>
        </details>
      </article>
    </div>

    <p v-if="!displayedPackages.length" class="package-empty">{{ copy('No package matches these filters.', 'Aucun package ne correspond à ces filtres.') }}</p>
    <button v-if="visiblePackages.length > displayedPackages.length" type="button" class="show-more" @click="limit += 12">
      {{ copy(`Show more (${displayedPackages.length}/${visiblePackages.length})`, `Afficher plus (${displayedPackages.length}/${visiblePackages.length})`) }}
    </button>
  </section>
</template>

<script>
import gameIcons from '@/data/gameIconManifest.json'
import { SURVIVOR_EQUIPMENT_SCENARIOS } from '@/data/survivorEquipmentKnowledge'
import {
  createEquipmentPackageCatalog,
  equipmentPackageMetrics,
  equipmentScenarioLabel,
  equipmentScenarioText
} from '@/lib/equipmentPackageEngine'
import { localizedEntryName } from '@/lib/loadoutLocalization'

const CATEGORY_IDS = [1, 3, 5, 6, 7, 8]
const CATEGORY_SOURCES = {
  1: 'https://deadbydaylight.wiki.gg/wiki/Keys',
  3: 'https://deadbydaylight.wiki.gg/wiki/Blinding',
  5: 'https://deadbydaylight.wiki.gg/wiki/Maps',
  6: 'https://deadbydaylight.wiki.gg/wiki/Med-Kit',
  7: 'https://deadbydaylight.wiki.gg/wiki/Toolboxes',
  8: 'https://deadbydaylight.wiki.gg/wiki/Fog_Vial'
}

export default {
  name: 'EquipmentPackageLab',
  data () {
    return {
      catalog: createEquipmentPackageCatalog(),
      metrics: equipmentPackageMetrics(),
      categories: CATEGORY_IDS,
      categoryFilter: '6',
      scenarioFilter: 'all',
      search: '',
      limit: 12
    }
  },
  computed: {
    availableScenarios () {
      return SURVIVOR_EQUIPMENT_SCENARIOS.filter(scenario => (
        this.categoryFilter === 'all' || String(scenario.category) === this.categoryFilter
      ))
    },
    visiblePackages () {
      const query = this.normalize(this.search)
      return this.catalog
        .filter(equipmentPackage => this.categoryFilter === 'all' || String(equipmentPackage.category) === this.categoryFilter)
        .map(equipmentPackage => {
          const result = this.scenarioFilter === 'all'
            ? equipmentPackage.scenarioScores[0]
            : equipmentPackage.scenarioScores.find(candidate => candidate.scenario.id === this.scenarioFilter)
          return result ? { package: equipmentPackage, result } : null
        })
        .filter(Boolean)
        .filter(entry => {
          if (!query) return true
          const haystack = [
            this.entryName(entry.package.item, 'item'),
            entry.package.item.id,
            this.scenarioName(entry.result.scenario)
          ].concat(entry.package.addons.reduce((names, addon) => (
            names.concat(this.entryName(addon, 'addon'), addon.id)
          ), [])).join(' ')
          return this.normalize(haystack).indexOf(query) !== -1
        })
        .sort((first, second) => (
          second.result.score - first.result.score ||
          second.package.consistency - first.package.consistency ||
          second.package.confidence - first.package.confidence ||
          first.package.id.localeCompare(second.package.id)
        ))
    },
    displayedPackages () {
      return this.visiblePackages.slice(0, this.limit)
    }
  },
  watch: {
    scenarioFilter () {
      this.limit = 12
    },
    search () {
      this.limit = 12
    }
  },
  methods: {
    copy (english, french) {
      return String(this.$i18n.locale || '').toLowerCase().indexOf('fr') === 0 ? french : english
    },
    normalize (value) {
      return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },
    setCategory (category) {
      this.categoryFilter = category
      if (this.scenarioFilter !== 'all' && !this.availableScenarios.some(scenario => scenario.id === this.scenarioFilter)) {
        this.scenarioFilter = 'all'
      }
      this.limit = 12
    },
    entryName (entry, type) {
      return localizedEntryName(entry, type, this.$i18n.locale)
    },
    categoryName (category) {
      const key = `forge.categories.${category}`
      return this.$te(key) ? this.$t(key) : String(category)
    },
    sourceUrl (category) {
      return CATEGORY_SOURCES[category] || CATEGORY_SOURCES[6]
    },
    scenarioName (scenario) {
      return equipmentScenarioLabel(scenario, this.$i18n.locale)
    },
    scenarioDescription (scenario) {
      return equipmentScenarioText(scenario, 'descriptions', this.$i18n.locale)
    },
    scenarioCondition (scenario) {
      return equipmentScenarioText(scenario, 'conditions', this.$i18n.locale)
    },
    itemIcon (item) {
      return gameIcons.items[String(item.id)] || ''
    },
    addonIcon (addon) {
      return gameIcons.addons[String(addon.id)] || ''
    },
    initials (name) {
      return String(name || '').split(/\s+/).slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase()
    },
    formatScore (score) {
      return Number.isInteger(score) ? score : Number(score).toFixed(1)
    },
    tierLabel (tier) {
      return {
        s_plus: 'S+',
        s: 'S',
        a: 'A',
        b: 'B',
        c: 'C',
        niche: this.copy('Niche', 'Niche')
      }[tier] || tier
    },
    metricLabel (metric) {
      const labels = {
        uses: ['Uses', 'Utilisations'],
        auraSeconds: ['Aura', 'Aura'],
        interactionSeconds: ['Interaction', 'Interaction'],
        batterySeconds: ['Battery', 'Autonomie'],
        accuracyBonus: ['Aim accuracy', 'Précision'],
        reachBonus: ['Beam reach', 'Portée'],
        beamWidthBonus: ['Beam width', 'Largeur'],
        blindDurationBonus: ['Blind duration', 'Durée aveuglement'],
        charges: ['Charges', 'Charges'],
        completeSelfHeals: ['Full self-heals', 'Auto-soins complets'],
        healSeconds: ['Heal time', 'Temps de soin'],
        altruisticSpeedBonus: ['Altruistic speed', 'Vitesse altruiste'],
        skillCheckChanceBonus: ['Skill Check chance', 'Chance de skill check'],
        greatZoneBonus: ['Great zone', 'Zone de Great'],
        greatProgressBonus: ['Great progress', 'Progression de Great'],
        generatorProgress: ['Generator progress', 'Progression générateur'],
        repairSpeedBonus: ['Repair speed', 'Vitesse de réparation'],
        deliverySeconds: ['Charge delivery', 'Transfert des charges'],
        generatorTimeSaved: ['Generator time saved', 'Temps générateur gagné'],
        sabotageSeconds: ['Sabotage time', 'Temps de sabotage'],
        sabotageActions: ['Hook sabotages', 'Sabotages de crochet'],
        notificationSuppressed: ['Noise alert', 'Alerte sonore'],
        generatorRequirementReduction: ['Generator requirement', 'Réduction requise du générateur'],
        noiseRadiusReduction: ['Noise radius', 'Rayon sonore'],
        sabotageSpeedBonus: ['Sabotage speed', 'Vitesse de sabotage'],
        extraHookRespawnSeconds: ['Extra Hook downtime', 'Indisponibilité du crochet'],
        radiusMetres: ['Radius', 'Rayon'],
        killerDetectionRadius: ['Killer detection', 'Détection du tueur'],
        durationSeconds: ['Duration', 'Durée'],
        deploySeconds: ['Deployment', 'Déploiement'],
        rechargeSeconds: ['Recharge', 'Recharge'],
        opacityPercent: ['Opacity', 'Opacité']
      }
      const label = labels[metric] || [metric, metric]
      return this.copy(label[0], label[1])
    },
    metricSuffix (metric) {
      if (['auraSeconds', 'interactionSeconds', 'batterySeconds', 'healSeconds', 'deliverySeconds', 'generatorTimeSaved', 'sabotageSeconds', 'durationSeconds', 'deploySeconds', 'rechargeSeconds', 'extraHookRespawnSeconds'].indexOf(metric) !== -1) return 's'
      if (['radiusMetres', 'killerDetectionRadius', 'noiseRadiusReduction'].indexOf(metric) !== -1) return 'm'
      return ''
    },
    riskLabel (risk) {
      const labels = {
        mutual_aura: ['Mutual aura reveal', 'Révélation d’aura mutuelle'],
        reduced_duration: ['Reduced duration', 'Durée réduite'],
        reduced_charges: ['Reduced charges', 'Charges réduites'],
        reduced_range: ['Reduced range', 'Portée réduite'],
        reduced_width: ['Narrower beam', 'Faisceau plus étroit'],
        faster_depletion: ['Faster depletion', 'Consommation accrue'],
        consumes_item: ['Consumes the Item', 'Consomme l’item'],
        requires_exhaustion: ['Requires Exhaustion', 'Demande Épuisement'],
        consumed_on_use: ['Consumed on use', 'Consommé à l’usage'],
        difficult_skill_check: ['Difficult Skill Check', 'Skill check difficile'],
        removes_regular_skill_checks: ['Removes regular Skill Checks', 'Supprime les skill checks normaux'],
        reduced_area: ['Reduced area', 'Zone réduite']
      }
      const label = labels[risk] || [risk.replace(/_/g, ' '), risk.replace(/_/g, ' ')]
      return this.copy(label[0], label[1])
    }
  }
}
</script>

<style lang="scss" scoped>
.equipment-lab { --cyan: #68dcff; --coral: #ff7f6d; margin: 12px 0; padding: clamp(18px, 3vw, 34px); border: 1px solid rgba(255,255,255,.1); border-radius: 28px; background: radial-gradient(circle at 90% 0, rgba(104,220,255,.1), transparent 30%), linear-gradient(155deg, #10171b, #080b0d); color: #f4f3ed; }
.eyebrow { color: var(--cyan); font-size: 9px; font-weight: 900; letter-spacing: .18em; text-transform: uppercase; }
.equipment-lab__hero { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(380px, .6fr); gap: 28px; align-items: end; }
.equipment-lab__hero h2 { max-width: 780px; margin: 10px 0 13px; font-size: clamp(29px, 4vw, 52px); letter-spacing: -.055em; line-height: .95; }
.equipment-lab__hero p { max-width: 760px; margin: 0; color: rgba(255,255,255,.56); line-height: 1.65; }
.equipment-lab__stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 7px; }
.equipment-lab__stats article { min-height: 92px; padding: 13px; border: 1px solid rgba(255,255,255,.08); border-radius: 15px; background: rgba(0,0,0,.2); display: flex; flex-direction: column; justify-content: center; }
.equipment-lab__stats strong { color: #fff; font-size: 25px; }
.equipment-lab__stats span { margin-top: 5px; color: rgba(255,255,255,.38); font-size: 7px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.equipment-lab__controls { margin-top: 25px; display: grid; grid-template-columns: minmax(240px, 1fr) minmax(240px, .65fr); gap: 9px; }
.equipment-lab__controls label > span { margin: 0 0 6px 2px; color: rgba(255,255,255,.43); display: block; font-size: 8px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.equipment-lab__controls input, .equipment-lab__controls select { width: 100%; height: 44px; padding: 0 13px; border: 1px solid rgba(255,255,255,.12); border-radius: 11px; background: #070a0c; color: #fff; outline: none; box-sizing: border-box; }
.equipment-lab__controls input:focus, .equipment-lab__controls select:focus { border-color: rgba(104,220,255,.55); }
.category-tabs { margin: 10px 0 18px; padding: 4px; border: 1px solid rgba(255,255,255,.08); border-radius: 13px; background: rgba(0,0,0,.18); display: flex; gap: 3px; overflow-x: auto; }
.category-tabs button { min-width: max-content; height: 36px; padding: 0 13px; border: 0; border-radius: 9px; background: transparent; color: rgba(255,255,255,.45); cursor: pointer; font-size: 8px; font-weight: 900; letter-spacing: .05em; text-transform: uppercase; }
.category-tabs button.is-active { background: #eef0eb; color: #101315; }
.package-ledger { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 9px; }
.package-card { min-width: 0; padding: 15px; border: 1px solid rgba(255,255,255,.09); border-radius: 18px; background: linear-gradient(145deg, rgba(255,255,255,.045), rgba(255,255,255,.012)); }
.package-card__header { min-height: 66px; display: grid; grid-template-columns: 55px minmax(0, 1fr) auto; gap: 11px; align-items: center; }
.package-card__icon, .addon-pair div > span { border: 1px solid rgba(104,220,255,.28); background: rgba(104,220,255,.06); display: grid; place-items: center; overflow: hidden; transform: rotate(45deg); }
.package-card__icon { width: 45px; height: 45px; margin-left: 5px; }
.package-card__icon img { width: 54px; height: 54px; object-fit: contain; transform: rotate(-45deg); }
.package-card__icon b { font-size: 9px; transform: rotate(-45deg); }
.package-card__header small, .package-card__scenario small { color: rgba(255,255,255,.38); font-size: 7px; font-weight: 900; letter-spacing: .1em; text-transform: uppercase; }
.package-card__header h3 { margin: 3px 0 5px; font-size: 15px; line-height: 1.15; }
.scenario-tier { width: max-content; padding: 2px 6px; border-radius: 5px; background: rgba(104,220,255,.09); color: var(--cyan); display: block; font-size: 8px; font-weight: 950; }
.scenario-tier.is-s_plus, .scenario-tier.is-s { background: rgba(130,232,186,.11); color: #82e8ba; }
.scenario-tier.is-b, .scenario-tier.is-c, .scenario-tier.is-niche { background: rgba(255,196,84,.09); color: #ffc454; }
.strength-dial { min-width: 53px; text-align: right; }
.strength-dial small, .strength-dial span { display: block; color: rgba(255,255,255,.3); font-size: 6px; text-transform: uppercase; }
.strength-dial strong { color: #82e8ba; font-size: 25px; letter-spacing: -.05em; }
.package-card__scenario { min-height: 126px; margin-top: 14px; padding: 13px; border: 1px solid rgba(255,255,255,.07); border-radius: 13px; background: rgba(0,0,0,.18); box-sizing: border-box; }
.package-card__scenario strong { margin-top: 5px; display: block; font-size: 13px; }
.package-card__scenario p { margin: 7px 0 0; color: rgba(255,255,255,.55); font-size: 10px; line-height: 1.5; }
.addon-pair { margin: 13px 0; display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
.addon-pair > div { min-width: 0; min-height: 50px; padding: 7px; border: 1px solid rgba(255,127,109,.14); border-radius: 11px; display: grid; grid-template-columns: 33px minmax(0, 1fr); gap: 8px; align-items: center; }
.addon-pair div > span { width: 28px; height: 28px; border-color: rgba(255,127,109,.28); background: rgba(255,127,109,.06); }
.addon-pair img { width: 34px; height: 34px; object-fit: contain; transform: rotate(-45deg); }
.addon-pair b { font-size: 6px; transform: rotate(-45deg); }
.addon-pair strong { font-size: 9px; line-height: 1.25; }
.package-card__metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5px; }
.package-card__metrics span, .potential-metrics span { padding: 7px; border: 1px solid rgba(255,255,255,.06); border-radius: 9px; background: rgba(255,255,255,.025); }
.package-card__metrics small, .potential-metrics small { color: rgba(255,255,255,.34); display: block; font-size: 6px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; }
.package-card__metrics strong, .potential-metrics strong { margin-top: 3px; color: #fff; display: block; font-size: 12px; }
.potential-metrics { margin-top: 6px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; }
.package-card__details { margin-top: 10px; border-top: 1px solid rgba(255,255,255,.07); }
.package-card__details summary { padding-top: 10px; color: rgba(255,255,255,.55); cursor: pointer; font-size: 8px; font-weight: 850; letter-spacing: .05em; text-transform: uppercase; }
.package-card__details p { color: rgba(255,255,255,.53); font-size: 9px; line-height: 1.5; }
.package-card__details > small { color: rgba(130,232,186,.56); font-size: 7px; text-transform: uppercase; }
.package-card__details > a { margin-left: 8px; color: var(--cyan); font-size: 7px; font-weight: 800; text-decoration: none; text-transform: uppercase; }
.risk-list { margin: 7px 0; display: flex; flex-wrap: wrap; gap: 4px; }
.risk-list span { padding: 4px 6px; border: 1px solid rgba(255,127,109,.2); border-radius: 6px; color: #ff9d8f; font-size: 7px; }
.show-more { width: 100%; height: 45px; margin-top: 10px; border: 1px solid rgba(104,220,255,.22); border-radius: 11px; background: rgba(104,220,255,.06); color: var(--cyan); cursor: pointer; font-size: 9px; font-weight: 900; text-transform: uppercase; }
.package-empty { color: rgba(255,255,255,.45); text-align: center; }
@media (max-width: 1180px) { .package-ledger { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 820px) { .equipment-lab__hero { grid-template-columns: 1fr; } .equipment-lab__controls { grid-template-columns: 1fr; } }
@media (max-width: 620px) { .package-ledger { grid-template-columns: 1fr; } .equipment-lab__stats { grid-template-columns: 1fr 1fr 1fr; } .equipment-lab__stats article { min-width: 0; padding: 9px; } .equipment-lab__stats strong { font-size: 20px; } }
</style>
